import { writable, derived, get } from 'svelte/store'
import type { Segment } from './aiFeatures'

export interface InlineView {
  id: string
  originalText: string
  suggestedText?: string
  position: {
    start: number
    end: number
    line?: number
    element?: HTMLElement
  }
  mode: 'paraphrase' | 'expand' | 'shrink' | 'continue' | 'prompt'
  type: 'card' | 'panel' // card for short text, panel for paragraphs
  status: 'pending' | 'processing' | 'completed' | 'applied' | 'cancelled'
  segments?: Segment[]
  diff?: {
    additions: number
    deletions: number
  }
  history?: Array<{
    text: string
    baseText: string  // The text this suggestion is based on
    timestamp: number
    reason?: string
    mode?: 'paraphrase' | 'expand' | 'shrink' | 'continue' | 'prompt'  // The mode used for this operation
  }>
  historyIndex?: number  // Current position in history
  isActive: boolean
  createdAt: number
  customPrompt?: string  // Store the custom prompt if mode is 'prompt'
}

interface InlineViewState {
  views: InlineView[]
  activeViewId: string | null
  isProcessing: boolean
}

const initialState: InlineViewState = {
  views: [],
  activeViewId: null,
  isProcessing: false
}

function createInlineViewStore() {
  const { subscribe, set, update } = writable<InlineViewState>(initialState)

  return {
    subscribe,
    
    // Add a new inline view
    addView(view: Omit<InlineView, 'id' | 'createdAt' | 'status' | 'isActive'>) {
      update(state => {
        const newView: InlineView = {
          ...view,
          id: `inline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          status: 'pending',
          isActive: true,
          createdAt: Date.now(),
          history: [],  // Start with empty history - will be added when AI generates result
          historyIndex: -1  // No history yet
        }
        
        // Keep all views but set others as inactive
        const updatedViews = state.views.map(v => ({ ...v, isActive: false }))
        
        return {
          ...state,
          views: [...updatedViews, newView],
          activeViewId: newView.id
        }
      })
    },
    
    // Update a view
    updateView(viewId: string, updates: Partial<InlineView>) {
      update(state => ({
        ...state,
        views: state.views.map(view =>
          view.id === viewId
            ? { ...view, ...updates }
            : view
        )
      }))
    },
    
    // Set active view
    setActiveView(viewId: string | null) {
      update(state => ({
        ...state,
        activeViewId: viewId,
        views: state.views.map(v => ({
          ...v,
          isActive: v.id === viewId
        }))
      }))
    },
    
    // Apply a view's changes
    applyView(viewId: string) {
      update(state => ({
        ...state,
        views: state.views.map(view =>
          view.id === viewId
            ? { ...view, status: 'applied', isActive: false }
            : view
        ),
        activeViewId: state.activeViewId === viewId ? null : state.activeViewId
      }))
    },
    
    // Cancel a view
    cancelView(viewId: string) {
      update(state => ({
        ...state,
        views: state.views.filter(v => v.id !== viewId),
        activeViewId: state.activeViewId === viewId ? null : state.activeViewId
      }))
    },
    
    // Remove applied views
    clearAppliedViews() {
      update(state => ({
        ...state,
        views: state.views.filter(v => v.status !== 'applied')
      }))
    },
    
    // Clear all views
    clearAll() {
      set(initialState)
    },
    
    // Restore views from saved state (e.g., when switching tabs)
    restoreViews(views: InlineView[]) {
      set({
        views: views,
        activeViewId: views.find(v => v.isActive)?.id || null,
        isProcessing: false
      })
    },
    
    // Add to view history
    addToHistory(viewId: string, text: string, reason?: string, baseText?: string, mode?: 'paraphrase' | 'expand' | 'shrink' | 'continue' | 'prompt') {
      update(state => ({
        ...state,
        views: state.views.map(view => {
          if (view.id === viewId) {
            const history = view.history || []
            const historyIndex = view.historyIndex ?? history.length - 1
            
            // If we're not at the end of history, truncate future entries
            const newHistory = historyIndex < history.length - 1 
              ? history.slice(0, historyIndex + 1)
              : history
            
            // Get the base text - either provided or from current state
            const currentBaseText = baseText || 
              (historyIndex >= 0 && history[historyIndex] ? history[historyIndex].baseText : view.originalText)
            
            const updatedHistory = [...newHistory, {
              text,
              baseText: currentBaseText,
              timestamp: Date.now(),
              reason,
              mode
            }]
            
            return {
              ...view,
              suggestedText: text,
              history: updatedHistory,
              historyIndex: newHistory.length  // Point to the new entry
            }
          }
          return view
        })
      }))
    },
    
    // Undo to previous state
    undoView(viewId: string) {
      update(state => ({
        ...state,
        views: state.views.map(view => {
          if (view.id === viewId && view.history && view.history.length > 0) {
            const currentIndex = view.historyIndex ?? view.history.length - 1
            if (currentIndex > 0) {
              const newIndex = currentIndex - 1
              const historyEntry = view.history[newIndex]
              const newText = historyEntry.text
              const baseText = historyEntry.baseText
              const historyMode = historyEntry.mode || view.mode
              return {
                ...view,
                suggestedText: newText,
                historyIndex: newIndex,
                mode: historyMode,  // Restore the mode from history
                segments: view.segments?.map(s => ({
                  ...s,
                  original: baseText,  // Update the base for comparison
                  suggested: newText,
                  hasChanges: newText !== baseText,
                  mode: historyMode,  // Update segment mode too
                  reason: historyEntry.reason || ''  // Update reason from history
                  // Keep segment's own history intact - don't sync with InlineView history
                }))
              }
            }
          }
          return view
        })
      }))
    },
    
    // Redo to next state
    redoView(viewId: string) {
      update(state => ({
        ...state,
        views: state.views.map(view => {
          if (view.id === viewId && view.history && view.history.length > 0) {
            const currentIndex = view.historyIndex ?? view.history.length - 1
            if (currentIndex < view.history.length - 1) {
              const newIndex = currentIndex + 1
              const historyEntry = view.history[newIndex]
              const newText = historyEntry.text
              const baseText = historyEntry.baseText
              const historyMode = historyEntry.mode || view.mode
              return {
                ...view,
                suggestedText: newText,
                historyIndex: newIndex,
                mode: historyMode,  // Restore the mode from history
                segments: view.segments?.map(s => ({
                  ...s,
                  original: baseText,  // Update the base for comparison
                  suggested: newText,
                  hasChanges: newText !== baseText,
                  mode: historyMode,  // Update segment mode too
                  reason: historyEntry.reason || ''  // Update reason from history
                  // Keep segment's own history intact - don't sync with InlineView history
                }))
              }
            }
          }
          return view
        })
      }))
    },
    
    // Check if undo is available
    canUndo(viewId: string): boolean {
      const state = get({ subscribe })
      const view = state.views.find(v => v.id === viewId)
      if (!view || !view.history || view.history.length === 0) {
        return false
      }
      const currentIndex = view.historyIndex ?? view.history.length - 1
      return currentIndex > 0
    },
    
    // Check if redo is available
    canRedo(viewId: string): boolean {
      const state = get({ subscribe })
      const view = state.views.find(v => v.id === viewId)
      if (!view || !view.history || view.history.length === 0) return false
      const currentIndex = view.historyIndex ?? view.history.length - 1
      return currentIndex < view.history.length - 1
    },
    
    // Set processing state
    setProcessing(isProcessing: boolean) {
      update(state => ({ ...state, isProcessing }))
    },
    
    // Determine view type based on text length
    determineViewType(text: string): 'card' | 'panel' {
      // Always use card for selected text to ensure it works
      // Panel mode should only be triggered when user explicitly wants split view
      // or when processing entire document without selection
      return 'card'
    },
    
    // Get active views (not applied or cancelled)
    getActiveViews() {
      const state = get({ subscribe })
      return state.views.filter(v => 
        v.status !== 'applied' && 
        v.status !== 'cancelled'
      )
    },
    
    // Get view by position
    getViewByPosition(start: number, end: number) {
      const state = get({ subscribe })
      return state.views.find(v => 
        v.position.start === start && 
        v.position.end === end &&
        v.status !== 'applied'
      )
    },
    
    // Clean up views that have been invalidated by text changes
    cleanupInvalidViews(currentText: string) {
      update(state => {
        const validViews = state.views.filter(view => {
          // Skip cancelled views but check applied views
          if (view.status === 'cancelled') {
            return true
          }
          
          // For applied views, check if the applied text is still present
          if (view.status === 'applied') {
            // If we have suggested text, check if it's still at the expected position
            if (view.suggestedText) {
              const expectedStart = view.position.start
              const expectedEnd = view.position.start + view.suggestedText.length
              
              // Check bounds
              if (expectedStart < 0 || expectedEnd > currentText.length) {
                return false  // Remove if position is invalid
              }
              
              const currentTextAtPosition = currentText.substring(expectedStart, expectedEnd)
              
              // If the applied text is not there anymore (e.g., after undo), remove the view
              if (currentTextAtPosition !== view.suggestedText) {
                return false
              }
            }
            return true
          }
          
          // Check if the position is still valid
          if (view.position.start < 0 || view.position.end > currentText.length) {
            // Try to find the text elsewhere before giving up
            const foundIndex = currentText.indexOf(view.originalText)
            if (foundIndex !== -1) {
              // Text found elsewhere - update position instead of removing
              view.position.start = foundIndex
              view.position.end = foundIndex + view.originalText.length
              return true
            }
            return false
          }
          
          // Check if the text at the position has changed
          const currentTextAtPosition = currentText.substring(
            view.position.start, 
            view.position.end
          )
          
          // If the text at this position is different from the original
          if (currentTextAtPosition !== view.originalText) {
            // Check if it's just whitespace differences
            const normalizedCurrent = currentTextAtPosition.replace(/\s+/g, ' ').trim()
            const normalizedOriginal = view.originalText.replace(/\s+/g, ' ').trim()
            
            if (normalizedCurrent === normalizedOriginal) {
              // Same content, just whitespace differences - update the original text
              view.originalText = currentTextAtPosition
              return true
            }
            
            // Try to find the exact original text elsewhere
            const foundIndex = currentText.indexOf(view.originalText)
            if (foundIndex !== -1) {
              // Text found elsewhere - update position
              view.position.start = foundIndex
              view.position.end = foundIndex + view.originalText.length
              return true
            }
            
            // Text really changed or disappeared
            return false
          }
          
          return true
        })
        
        return {
          ...state,
          views: validViews,
          // Clear active view if it was removed
          activeViewId: validViews.some(v => v.id === state.activeViewId) 
            ? state.activeViewId 
            : null
        }
      })
    }
  }
}

export const inlineViewStore = createInlineViewStore()

// Derived stores
export const activeInlineView = derived(
  inlineViewStore,
  $store => $store.views.find(v => v.id === $store.activeViewId) || null
)

export const hasActiveInlineViews = derived(
  inlineViewStore,
  $store => $store.views.some(v => 
    v.status !== 'applied' && v.status !== 'cancelled'
  )
)

export const inlineViewsByType = derived(
  inlineViewStore,
  $store => {
    const cardViews = $store.views.filter(v => v.type === 'card')
    const panelViews = $store.views.filter(v => v.type === 'panel')
    return { cardViews, panelViews }
  }
)